// externals
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';

// internals
import {
    hideRecentDeals,
    openDeal as openDealAction,
    redirectToDealList as redirectToDealListAction,
    createQuote as createQuoteAction
} from '../store/actionCreators';
import { createQuoteDealListClicked } from '@makemydeal/dr-dash-store/actions/recentDealsAnalyticsActionCreators';
import { useRecentDealsSelector } from './useRecentDealsSelector';
import { useFetchRecentDeals } from './useFetchRecentDeals';
import { OpenInTarget } from '../enum/common.enum';
import { ApiRequestStatus } from '@makemydeal/dr-shared-types';
import { useMemoizedRecentDeals } from './useMemoizedRecentDeals';
import { useDealershipSelection } from './useDealershipSelection';

/**
 * Main hook for managing recent deals functionality.
 * Composes smaller, focused hooks following the Single Responsibility Principle (SRP).
 *
 * This hook orchestrates multiple concerns:
 * - Data fetching (useFetchRecentDeals) - HOW to fetch
 * - Data transformation (useMemoizedRecentDeals)
 * - Dealership selection (useDealershipSelection)
 * - Fetch triggering (onload + on-demand) - WHEN to fetch
 *
 * @returns Object containing recent deals state and actions
 */
export const useRecentDeals = () => {
    const dispatch = useDispatch();
    const { deals, requestStatus, showRecentDeals, errorDetails } = useRecentDealsSelector();
    const isQuoteModeEnabled = useSelector(featureToggleSelectors.isManagerViewQuoteModeEnabled);

    // Delegate data fetching to dedicated hook (SRP) - only knows HOW to fetch
    const { refetchDeals } = useFetchRecentDeals();

    // Delegate dealership selection to dedicated hook (SRP) - inject refetchDeals via DI
    const { selectedDealership, handleChangeDealership, availableDealershipsCount, isDealershipsLoading } =
        useDealershipSelection(refetchDeals);

    // Delegate data transformation to dedicated hook (SRP)
    const { modifiedDeals, hasOpenDealEnabled } = useMemoizedRecentDeals();

    const { mmdId, coOrgId } = selectedDealership || {};

    useEffect(() => {
        const isValidMmdId = mmdId && mmdId !== '-1';
        const shouldFetch = coOrgId && isValidMmdId && requestStatus === ApiRequestStatus.Idle && deals.length === 0;

        if (shouldFetch) {
            refetchDeals(coOrgId, mmdId as string);
        }
    }, [requestStatus, deals.length, mmdId]);

    /**
     * Dispatches action to navigate back to the deal list.
     * Host middleware intercepts this action and handles navigation (DIP).
     */
    const redirectToDealList = useCallback(() => {
        dispatch(redirectToDealListAction());
    }, [dispatch]);

    /**
     * Dispatches action to open a specific deal.
     * If no dealId is provided, opens the currently selected deal.
     * Host middleware intercepts this action and handles navigation (DIP).
     *
     * @param dealId - Optional deal ID to open; defaults to selected deal
     * @param target - Optional target for navigation using OpenInTarget enum:
     *   - OpenInTarget.SELF: Opens deal in same tab
     *   - OpenInTarget.BLANK: Opens deal in new tab
     */
    const openDeal = useCallback(
        (dealId?: string, target?: OpenInTarget) => {
            dispatch(hideRecentDeals());
            dispatch(openDealAction(dealId, target));
        },
        [dispatch, modifiedDeals]
    );

    /**
     * Opens a deal in a new tab.
     * Convenience wrapper around openDeal with OpenInTarget.BLANK preset.
     *
     * @param dealId - The deal ID to open in a new tab
     */
    const handleOpenDealInNewTab = useCallback(
        (dealId?: string) => {
            // Resolve dealId: use provided value or fallback to currently selected deal
            const resolvedDealId = dealId ?? modifiedDeals.find((deal) => deal.isSelected)?.dealId;

            openDeal(resolvedDealId, OpenInTarget.BLANK);
        },
        [openDeal]
    );

    /**
     * Closes the recent deals panel without any navigation.
     */
    const handleCancel = useCallback(() => {
        dispatch(hideRecentDeals());
    }, [dispatch]);

    /**
     * Dispatches action to create a quote for the currently selected dealership.
     * Closes the panel and delegates to host middleware, which opens a new
     * Manager View tab bootstrapped from the org.
     */
    const handleCreateQuote = useCallback(() => {
        dispatch(createQuoteDealListClicked());
        dispatch(hideRecentDeals());
        dispatch(createQuoteAction(coOrgId));
    }, [dispatch, coOrgId]);

    // Combine loading states - show loading if either dealerships or deals are loading
    const isLoading = requestStatus === ApiRequestStatus.Loading || isDealershipsLoading;

    return {
        /** Indicates if recent deals or dealerships are currently being fetched */
        isLoading,
        /** Controls visibility of the recent deals panel */
        showRecentDeals,
        /** Transformed list of recent deals with computed properties */
        recentDeals: modifiedDeals,
        /** Indicates if opening a deal is enabled */
        hasOpenDealEnabled,
        /** Display title with deal count (quotes filtered out when Quote Mode is off, so it matches the rows shown) */
        recentDealsTitle: `Recent Deals ${modifiedDeals.length ? `(${modifiedDeals.length})` : ''}`,
        /** Error details if fetch failed */
        errorDetails,
        /** Currently active dealership MMDID (selected or current deal's) */
        selectedDealership,
        /** Show dealership picker */
        showDealershipPicker: availableDealershipsCount > 1,
        /** Action to navigate back to deal list */
        redirectToDealList,
        /** Action to open a specific or selected deal */
        openDeal,
        /** Action to open a deal in a new tab */
        handleOpenDealInNewTab,
        /** Action to handle dealership change (on-demand fetch via DI) */
        handleChangeDealership,
        /** Whether Quote Mode is enabled (controls Cancel / Create a Quote footer buttons) */
        isQuoteModeEnabled,
        /** Action to close the panel without navigating */
        handleCancel,
        /** Action to create a quote for the selected dealership in a new tab */
        handleCreateQuote
    };
};
